import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@mantine/core';
import { useEventListener } from 'ahooks';
import { translate } from '@shakkerai/util-i18n';
import { useTempState } from '@starflow/app/store';
import { config } from '@shakkerai/config';
import { Icon } from '@shakkerai/components';
import { ImageWindowWrapper } from '../base/Wrapper';
import { ImageList } from '../base/List';
import { updatePartialState, usePartialState } from '../../store/state';
import { loadModelImages, loadMoreModelImages } from '../../store/actions';
import iconLatestTagCN from '../../assets/latest_tag_cn.svg';
import iconLatestTagEN from '../../assets/latest_tag_en.svg';

const isOverseas = config.env === 'aws';
const latestTag = isOverseas ? iconLatestTagEN : iconLatestTagCN;

const ModelImagesWindowInner = React.memo(({ modelUuid, versionUuid: initialVersionUuid }) => {
  const [tab, setTab] = useState('case');
  const listWrapperRef = useRef(null);
  const caseImagesWrapperRef = useRef(null);
  // const [scrollOffset, setScrollOffset] = useState(0);
  const modelData = usePartialState('modelImages.modelData');
  const caseImages = usePartialState('modelImages.case.data');
  const moreImages = usePartialState('modelImages.more.data');
  const [versionUuid, setVersionUuid] = useState(initialVersionUuid);
  const [versionOptionsOpened, setVersionOptionsOpened] = useState(false);

  const isSceneControlModel = modelData?.isSceneControlModel;

  const handleSwitchTab = useCallback((tab) => {
    if (tab === 'more') {
      listWrapperRef.current.scrollTo({ top: caseImagesWrapperRef.current.clientHeight, behavior: 'smooth' });
    } else {
      listWrapperRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleSwitchVersion = useCallback(
    (event) => {
      const versionUuid = event.currentTarget.dataset.uuid;
      setVersionUuid(versionUuid);
      updatePartialState(`modelImages.case.data`, { items: [] });
      updatePartialState(`modelImages.more.data`, { items: [] });

      loadModelImages(versionUuid, modelUuid, 'case').then(() => {
        loadModelImages(versionUuid, modelUuid, 'more');
      });

      setVersionOptionsOpened(false);
      handleSwitchTab('case');

      event.stopPropagation();
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modelUuid],
  );

  const handleScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    // 判断社区图片是否滚动到了顶部
    const caseImagesHeight = caseImagesWrapperRef.current.clientHeight;
    if (scrollTop >= caseImagesHeight - 10) {
      setTab('more');
    } else {
      setTab('case');
    }

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      loadMoreModelImages('more');
    }
  }, []);

  const handleGlobalClick = useCallback((event) => {
    if (!event.target?.closest(`.model-images-versions`)) {
      setVersionOptionsOpened(false);
      return;
    }
  }, []);

  const versionOptions = useMemo(() => {
    if (!modelData?.versions) return [];

    const options = modelData.versions.map((item) => ({
      value: item.uuid,
      label: item.name,
    }));

    // if (tab === 'more') {
    //   options.unshift({
    //     value: 'all',
    //     label: translate('model_gallery_version_all'),
    //   });
    // }

    return options;
  }, [modelData]);

  const selectedVersionLabel = useMemo(() => {
    return versionOptions.find((item) => item.value === versionUuid)?.label || '-';
  }, [versionUuid, versionOptions]);

  const isLatestVersion = versionUuid !== 'all' && versionUuid === versionOptions[0]?.value;

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    if (initialVersionUuid) {
      updatePartialState(`modelImages.case.data`, { items: [] });
      updatePartialState(`modelImages.more.data`, { items: [] });

      loadModelImages(initialVersionUuid, modelUuid, 'case').then(() => {
        loadModelImages(initialVersionUuid, modelUuid, 'more');
      });
    }
  }, [modelUuid, initialVersionUuid]);

  return (
    <div className="flex flex-col items-stretch flex-1 overflow-hidden">
      <div className="flex items-center px-4 py-3">
        <button
          disabled={tab === 'case'}
          onClick={() => handleSwitchTab('case')}
          className="flex-shrink-0 h-[26px] mr-2 px-[18px] bg-[#f4f6f8] disabled:bg-primary/10 rounded-[2px] border border-transparent disabled:border-primary text-xs text-[#2d2d2d]"
        >
          {translate('Modelgallery_title_Example')}
        </button>
        <button
          disabled={tab === 'more'}
          onClick={() => handleSwitchTab('more')}
          className="flex-shrink-0 h-[26px] px-[18px] bg-[#f4f6f8] disabled:bg-primary/10 rounded-[2px] border border-transparent disabled:border-primary text-xs text-[#2d2d2d]"
        >
          {translate('Modelgallery_title_Gallery')}
        </button>
        <div className="flex-1" />
        {modelData && (
          <Popover opened={versionOptionsOpened} position="bottom-end">
            <Popover.Target>
              <button
                className="model-images-versions flex items-center ml-4 overflow-hidden"
                onClick={() => setVersionOptionsOpened(!versionOptionsOpened)}
              >
                {isLatestVersion && <img draggable={false} className="mr-1" src={latestTag} />}
                <span className="overflow-hidden text-ellipsis whitespace-nowrap mr-1 text-xs text-[#2d2d2d]">
                  {selectedVersionLabel}
                </span>
                <Icon
                  fontSize={12}
                  color="#2d2d2d"
                  className={versionOptionsOpened ? 'icon-border_up' : 'icon-border_down'}
                />
              </button>
            </Popover.Target>
            <Popover.Dropdown className="px-0 py-1 bg-[#2c2c2c] rounded-[2px] border-none overflow-hidden">
              <div className="flex flex-col">
                {versionOptions.map((item) => (
                  <button
                    key={item.value}
                    data-uuid={item.value}
                    onClick={handleSwitchVersion}
                    disabled={versionUuid === item.value}
                    className="flex items-center justify-between w-full py-1.5 px-5 text-white disabled:bg-primary disabled:text-[#2d2d2d]"
                  >
                    <span className="text-xs">{item.label}</span>
                  </button>
                ))}
              </div>
            </Popover.Dropdown>
          </Popover>
        )}
      </div>
      <div ref={listWrapperRef} onScroll={handleScroll} className="flex-1 overflow-auto tiny-scrollbar px-4 pb-2">
        <div ref={caseImagesWrapperRef} className="mb-2">
          <ImageList data={caseImages} withNormalLayout={isSceneControlModel} isModelImage showFooter={false} />
        </div>
        {moreImages.items.length && caseImages.items.length ? (
          <div className="flex items-center justify-center mt-5 mb-3">
            <div className="w-16 h-px bg-[#efefef]" />
            <span className="mx-3 text-[#2D2D2D] text-xs">{translate('Modelgallery_title_Gallery')}</span>
            <div className="w-16 h-px bg-[#efefef]" />
          </div>
        ) : null}
        <ImageList data={moreImages} isModelImage showFooter={false} />
      </div>
    </div>
  );
});

export const ModelImagesWindow = React.memo(() => {
  const modelImagesModelName = useTempState('modelImagesModelName');
  const modelImagesModelUuid = useTempState('modelImagesModelUuid');
  const modelImagesVersionUuid = useTempState('modelImagesVersionUuid');

  return (
    <ImageWindowWrapper type="modelImages" label={modelImagesModelName}>
      <ModelImagesWindowInner
        key={modelImagesModelUuid}
        modelUuid={modelImagesModelUuid}
        versionUuid={modelImagesVersionUuid}
      />
    </ImageWindowWrapper>
  );
});
